import React, { useState, useEffect } from 'react';
import { Gift, X } from 'lucide-react';

/**
 * Promotional banner component for top of proposals page
 * Dismissible and stores state in localStorage
 */
const PromoBanner = ({
  id = 'default-promo',
  message,
  ctaText,
  onCtaClick,
  icon: Icon = Gift,
  variant = 'info' // 'info' | 'success' | 'warning'
}) => {
  const [isDismissed, setIsDismissed] = useState(true);

  // Check localStorage for dismissed state
  useEffect(() => {
    const dismissed = localStorage.getItem(`promo-banner-${id}`);
    setIsDismissed(dismissed === 'true');
  }, [id]);

  const handleDismiss = () => {
    setIsDismissed(true);
    localStorage.setItem(`promo-banner-${id}`, 'true');
  };

  if (isDismissed) {
    return null;
  }

  // Variant styles
  const variantStyles = {
    info: 'bg-blue-50 border-blue-200',
    success: 'bg-green-50 border-green-200',
    warning: 'bg-yellow-50 border-yellow-200',
  };

  const iconStyles = {
    info: 'text-blue-500',
    success: 'text-green-500',
    warning: 'text-yellow-500',
  };

  const buttonStyles = {
    info: 'bg-blue-600 hover:bg-blue-700 text-white',
    success: 'bg-green-600 hover:bg-green-700 text-white',
    warning: 'bg-yellow-600 hover:bg-yellow-700 text-white',
  };

  return (
    <div className={`relative rounded-lg border p-4 ${variantStyles[variant]}`}>
      <div className="flex items-center justify-between gap-4">
        {/* Icon and Message */}
        <div className="flex items-center gap-3">
          <Icon className={`w-5 h-5 flex-shrink-0 ${iconStyles[variant]}`} />
          <p className="text-sm text-gray-700">{message}</p>
        </div>

        {/* CTA and Close */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {ctaText && onCtaClick && (
            <button
              onClick={onCtaClick}
              className={`px-4 py-1.5 text-sm font-medium rounded-lg transition-colors ${buttonStyles[variant]}`}
            >
              {ctaText}
            </button>
          )}
          <button
            onClick={handleDismiss}
            className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default PromoBanner;
